/**
 * Composable that can be used in every component for input validations.
 * Steps to use:
 * - Import this into the component import inputValidationMixin from 'some/path/composables/input-validations';
 * - Add it as a mixin: composalbes: [useInputValidations],
 * - When using the vuetify text-field component:
 *    <v-text-field v-model="someModel" :rules="[this.required]" />
 * - This will render the text field in an error state if it is empty.
 * - More rules can be added to the array if needed.
 */
import numberUtils from '@/utils/number-format-utils';
import { useFormatNumber } from '@/composables/use-format-number';
import { useInternationalization } from '@/composables/use-internationalization';

const { isEmpty, isFinite, includes } = require('lodash');

const parseValue = value => {
  // We need to parse numeric values using numberUtils.formatNumber since it's parsing
  // them according to the numeral locale instead of using the JS default which can cause
  // issues with decimal delimiters
  const stringValue = numberUtils.formatNumber({ number: value });

  // handle case where () format is used for negative numbers
  if (stringValue.startsWith('(') && stringValue.endsWith(')')) {
    return stringValue.substring(1, stringValue.length - 1);
  }
  return stringValue;
};

export function useInputValidations() {
  const { t } = useInternationalization();
  const { formatNumber } = useFormatNumber();

  function required(value) {
    return value === 0 || !!value || t('validationErrors.required');
  }

  function isInteger(value) {
    // AOV3-609 TODO: investigate why value is string after edit.
    const stringValue = parseValue(value);
    if (!numberUtils.validNumericString(stringValue)) return t('validationErrors.integer');
    const sanitizedValue = !isEmpty(stringValue)
      ? numberUtils.formatStringToNumber(value)
      : stringValue;
    return Number.isInteger(sanitizedValue) || t('validationErrors.integer');
  }

  function isNumber(value) {
    if (isEmpty(String(value))) return true;
    const stringValue = numberUtils.formatNumber({ number: value });

    if (!numberUtils.validNumericString(stringValue)) return t('validationErrors.number');
    const sanitizedValue = numberUtils.formatStringToNumber(stringValue);
    if (!isFinite(sanitizedValue)) {
      return t('validationErrors.number');
    }
    return true;
  }

  function isLessThan(value, maximum) {
    const sanitizedValue = numberUtils.formatStringToNumber(value);
    return (
      sanitizedValue <= maximum ||
      t('validationErrors.numberIsTooBig', [formatNumber({ number: maximum, format: 'float' })])
    );
  }

  function isAlphanumeric(value) {
    return /^[A-Za-z0-9]+$/.test(value) || t('validationErrors.alphanumeric');
  }

  function isPositive(value) {
    const strValue = parseValue(value);
    const sanitizedValue = numberUtils.formatStringToNumber(strValue);
    return (
      (isNumber(value) === true && sanitizedValue > 0) || t('validationErrors.numberNotPositive')
    );
  }

  function isNotEmpty(value) {
    return !isEmpty(String(value).trim()) || t('validationErrors.required');
  }

  function isGreaterThan(value, minimum) {
    if (isEmpty(String(value))) return true;
    const sanitizedValue = numberUtils.formatStringToNumber(value);
    return sanitizedValue > minimum || t('validationErrors.numberIsLessThan', [minimum]);
  }

  function isGreaterOrEqual(value, minimum) {
    if (isEmpty(String(value))) return true;
    const sanitizedValue = numberUtils.formatStringToNumber(value);
    return sanitizedValue >= minimum || t('validationErrors.numberMustBeBiggerOrEqual', [minimum]);
  }

  function isNameUnique(newName, currentName, nameList, entity) {
    // v-model.trim gotcha https://github.com/vuejs/vue/issues/5847
    const trimmedNewName = newName.trim();
    // It is allowed to be the same name, the filter would not handle this edge case.
    if (currentName === trimmedNewName) return true;
    if (includes(nameList, trimmedNewName)) {
      return t('validationErrors.unique', [entity]);
    }
    return true;
  }

  return {
    required,
    isInteger,
    isNumber,
    isLessThan,
    isAlphanumeric,
    isPositive,
    isNotEmpty,
    isGreaterThan,
    isGreaterOrEqual,
    isNameUnique,
  };
}
